/**
 * Axios HTTP client with JWT interceptor.
 * See Phase 15.2 build instructions.
 *
 * - Base URL from env or Vite proxy (/api)
 * - Attaches access token to every request
 * - Auto-refreshes on 401 and retries the original request
 * - Global error handler
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

const client = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ---------------------------------------------------------------------------
// Request interceptor — attach JWT access token
// ---------------------------------------------------------------------------

client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Response interceptor — auto-refresh on 401
// ---------------------------------------------------------------------------

let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retried, attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return client(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        // No refresh token — force logout
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${BASE_URL}/auth/refresh/`, {
          refresh: refreshToken,
        });

        const newAccessToken = data.data?.access || data.access;
        localStorage.setItem('access_token', newAccessToken);

        processQueue(null, newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return client(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

/**
 * Fetch every page of a paginated list endpoint and return the
 * concatenated array. Handles two response shapes:
 *
 *   - StandardPagination (apps.common.pagination):
 *       { data: [...], count: N, page: n, page_size: k }
 *   - DRF default PageNumberPagination (a few legacy endpoints):
 *       { count: N, next: url|null, previous: url|null, results: [...] }
 *
 * On the first request we ask for the max page_size the backend
 * accepts (StandardPagination.max_page_size = 1000; see
 * apps/common/pagination.py). One page usually satisfies any
 * realistic per-job list on this app; the follow-up loop is a
 * defense-in-depth safety net for the extreme case (>1000 rows).
 *
 * Existing callers that read data.data || data.results || [] can
 * be migrated to this helper without changing their return shape
 * (still an array).
 */
export async function fetchAllPages(path, params = {}) {
  const MAX_PAGE_SIZE = 1000;
  const first = await client.get(path, {
    params: { ...params, page_size: MAX_PAGE_SIZE },
  });
  const firstBody = first.data;
  const firstRows = firstBody.data || firstBody.results || [];
  const total = typeof firstBody.count === 'number' ? firstBody.count : firstRows.length;
  if (firstRows.length >= total) {
    return firstRows;
  }
  // Follow-up pages (only if the server actually capped below our
  // requested page_size AND there is more to fetch). Bounded loop —
  // stop after MAX_PAGES to avoid runaway even in pathological
  // cases; each MAX_PAGE_SIZE page holds 1000 rows.
  const MAX_PAGES = 50;
  const collected = [...firstRows];
  let page = 2;
  while (collected.length < total && page <= MAX_PAGES) {
    const resp = await client.get(path, {
      params: { ...params, page_size: MAX_PAGE_SIZE, page },
    });
    const rows = resp.data.data || resp.data.results || [];
    if (rows.length === 0) break;
    collected.push(...rows);
    page += 1;
  }
  return collected;
}


export default client;
